import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/** @type {WeakSet<HTMLFormElement>} */
const pendingFallbackAddToCartForms = new WeakSet();

/**
 * Collect all cart section ids that need to refresh after add-to-cart.
 * @returns {string[]}
 */
function getCartSectionsToUpdate() {
  const sectionsToUpdate = new Set();

  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionsToUpdate.add(item.dataset.sectionId);
    }
  });

  return Array.from(sectionsToUpdate);
}

/**
 * Extract the cart item count from rendered cart sections.
 * @param {Record<string, string> | undefined} sections
 * @returns {number | undefined}
 */
function getCartItemCountFromSections(sections) {
  if (!sections) return undefined;

  for (const sectionHtml of Object.values(sections)) {
    if (typeof sectionHtml !== 'string' || sectionHtml.length === 0) continue;

    const sectionDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const itemCountText = sectionDocument.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
    const itemCount = itemCountText ? Number.parseInt(itemCountText, 10) : Number.NaN;

    if (Number.isFinite(itemCount)) {
      return itemCount;
    }
  }

  return undefined;
}

function openCartDrawerImmediately() {
  const cartDrawer = document.querySelector('cart-drawer-component');
  if (!cartDrawer) return;

  if (typeof cartDrawer.open === 'function') {
    cartDrawer.open();
  }
}

/**
 * @param {string | null | undefined} value
 * @returns {string}
 */
function normalizeText(value) {
  return value?.replace(/\s+/g, ' ').trim() || '';
}

/**
 * @param {ParentNode | null | undefined} root
 * @param {string[]} selectors
 * @returns {HTMLElement | null}
 */
function getFirstMatchingElement(root, selectors) {
  if (!root || typeof root.querySelector !== 'function') return null;

  for (const selector of selectors) {
    const element = root.querySelector(selector);
    if (element instanceof HTMLElement) {
      return element;
    }
  }

  return null;
}

/**
 * @param {ParentNode | null | undefined} root
 * @param {string[]} selectors
 * @returns {string}
 */
function getTextFromSelectors(root, selectors) {
  const element = getFirstMatchingElement(root, selectors);
  return normalizeText(element?.textContent);
}

/**
 * @param {ParentNode | null | undefined} root
 * @param {string[]} selectors
 * @returns {string}
 */
function getHrefFromSelectors(root, selectors) {
  const element = getFirstMatchingElement(root, selectors);
  if (!(element instanceof HTMLAnchorElement)) return '';

  return element.getAttribute('href') || '';
}

/**
 * @param {ParentNode | null | undefined} root
 * @param {string[]} selectors
 * @returns {string}
 */
function getImageFromSelectors(root, selectors) {
  const element = getFirstMatchingElement(root, selectors);
  if (!(element instanceof HTMLImageElement)) return '';

  return element.currentSrc || element.src || '';
}

/**
 * @param {HTMLFormElement} form
 * @returns {string}
 */
function getSelectedOptionsText(form) {
  const selectedVariantCardTitle = getTextFromSelectors(form, [
    '[data-variant-card].is-active .bhilwara-product-section__variant-title',
  ]);

  if (selectedVariantCardTitle && selectedVariantCardTitle.toLowerCase() !== 'default title') {
    return selectedVariantCardTitle;
  }

  /** @type {string[]} */
  const optionValues = [];

  form
    .querySelectorAll('select[name^="options["], select[data-option-position]')
    .forEach((selectElement) => {
      if (!(selectElement instanceof HTMLSelectElement)) return;

      const selectedOption = selectElement.options[selectElement.selectedIndex];
      const optionValue = normalizeText(selectedOption?.textContent || selectElement.value);

      if (optionValue && optionValue.toLowerCase() !== 'default title') {
        optionValues.push(optionValue);
      }
    });

  form
    .querySelectorAll('input[type="radio"][name^="options["]:checked, input[type="radio"][data-option-name]:checked')
    .forEach((radioElement) => {
      if (!(radioElement instanceof HTMLInputElement)) return;

      const label =
        (radioElement.id ? form.querySelector(`label[for="${CSS.escape(radioElement.id)}"]`) : null) ||
        radioElement.closest('label');
      const optionValue = normalizeText(label?.textContent || radioElement.value);

      if (optionValue && optionValue.toLowerCase() !== 'default title') {
        optionValues.push(optionValue);
      }
    });

  return Array.from(new Set(optionValues)).join(' / ');
}

/**
 * @typedef {object} OptimisticCartItem
 * @property {string} token
 * @property {string} title
 * @property {string} variantTitle
 * @property {string} imageUrl
 * @property {string} productUrl
 * @property {string} priceText
 * @property {number} quantity
 * @property {string | undefined} productId
 * @property {string | undefined} variantId
 */

/**
 * @param {HTMLFormElement} form
 * @param {{ quantity: number, variantId?: string }} config
 * @returns {OptimisticCartItem | null}
 */
function getOptimisticCartItem(form, config) {
  const productFormComponent = form.closest('product-form-component');
  const quickAddComponent = form.closest('quick-add-component');
  const addToCartContainer =
    form.querySelector('add-to-cart-component') || productFormComponent?.querySelector('add-to-cart-component');
  const contextRoot =
    quickAddComponent?.closest('product-card') ||
    form.closest('.bhilwara-product-section, .mm-header-product-card, .handcrafted-grid__card, .recommendation-card, product-card') ||
    quickAddComponent ||
    form;

  const title = normalizeText(
    quickAddComponent?.getAttribute('data-product-title') ||
      productFormComponent?.getAttribute('data-product-title') ||
      getTextFromSelectors(contextRoot, [
        '.bhilwara-product-section__title',
        '.mm-header-product-card__title',
        '.handcrafted-grid__card-title',
        '.recommendation-card__title',
        '.view-product-title a',
        'product-title a',
        '[ref="productTitleLink"]',
        'h1',
        'h2',
        'h3',
      ]) ||
      document.title
  );

  if (!title) return null;

  const variantTitle = getSelectedOptionsText(form);
  const imageUrl =
    addToCartContainer?.getAttribute('data-product-variant-media') ||
    getImageFromSelectors(contextRoot, [
      '[data-main-image]',
      '.mm-header-product-card__image',
      '.handcrafted-grid__image',
      '.recommendation-card__image',
      '.product-media__image',
      'img',
    ]);
  const productUrl =
    productFormComponent?.getAttribute('data-product-url') ||
    getHrefFromSelectors(contextRoot, [
      '.mm-header-product-card__title',
      '.handcrafted-grid__card-overlay-link',
      '.recommendation-card__image-link',
      '.recommendation-card__title',
      '.view-product-title a',
      'product-title a',
      '[ref="productTitleLink"]',
      '.product-card__link',
      'a[href]',
    ]) ||
    (form.closest('.bhilwara-product-section') ? window.location.pathname + window.location.search : '');
  const priceText = getTextFromSelectors(contextRoot, [
    '[data-product-price]',
    '.mm-header-product-card__price-current',
    '.handcrafted-grid__price',
    '.recommendation-card__price',
    'product-price .price-item--sale',
    'product-price .price-item--regular',
    'product-price .price-item',
    '.price .price-item--sale',
    '.price .price-item',
  ]);

  return {
    token: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    title,
    variantTitle,
    imageUrl,
    productUrl,
    priceText,
    quantity: config.quantity,
    productId:
      form.closest('[data-product-id]')?.getAttribute('data-product-id') ||
      productFormComponent?.getAttribute('data-product-id') ||
      undefined,
    variantId: config.variantId,
  };
}

/**
 * @param {OptimisticCartItem | null} item
 */
function dispatchOptimisticCartAdd(item) {
  if (!item) return;

  document.dispatchEvent(
    new CustomEvent(ThemeEvents.cartAddPending, {
      detail: item,
    })
  );
}

/**
 * @param {HTMLFormElement} form
 * @returns {HTMLElement | null}
 */
function getFallbackErrorContainer(form) {
  const scope =
    form.closest(
      'product-form-component, .bhilwara-product-section__form-component, .handcrafted-grid__actions, .mm-header-product-card__form-component'
    ) || form.parentElement;

  if (!(scope instanceof Element)) return null;

  const errorContainer = scope.querySelector(
    '[ref="addToCartTextError"], .product-form-text__error, .bhilwara-product-section__form-status'
  );

  return errorContainer instanceof HTMLElement ? errorContainer : null;
}

/**
 * @param {HTMLFormElement} form
 * @param {string} message
 */
function showFallbackAddToCartError(form, message) {
  const errorContainer = getFallbackErrorContainer(form);
  if (!errorContainer) return;

  errorContainer.classList.remove('hidden');

  const textNode = Array.from(errorContainer.childNodes).find(
    (node) => node.nodeType === Node.TEXT_NODE && node.textContent != null
  );

  if (textNode) {
    textNode.textContent = ` ${message}`;
  } else {
    errorContainer.appendChild(document.createTextNode(` ${message}`));
  }
}

/**
 * @param {HTMLFormElement} form
 */
function clearFallbackAddToCartError(form) {
  const errorContainer = getFallbackErrorContainer(form);
  if (!errorContainer) return;

  errorContainer.classList.add('hidden');
}

/**
 * Handles add-to-cart forms that do not use the theme's product-form-component.
 * This keeps custom section buttons on-page and opens the cart drawer instead of redirecting to /cart.
 * @param {SubmitEvent} event
 */
function handleFallbackAddToCartSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (event.defaultPrevented) return;
  if (form.closest('product-form-component, quick-order-list-component')) return;

  const formAction = form.getAttribute('action') || '';
  const isAddToCartForm =
    form.dataset.type === 'add-to-cart-form' || formAction.includes('/cart/add') || form.querySelector('button[name="add"]');

  if (!isAddToCartForm) return;

  event.preventDefault();

  if (pendingFallbackAddToCartForms.has(form)) return;
  pendingFallbackAddToCartForms.add(form);

  const formData = new FormData(form);
  const quantity = Number(formData.get('quantity')) || 1;
  const variantId = formData.get('id');
  const sectionsToUpdate = getCartSectionsToUpdate();
  const optimisticCartItem = getOptimisticCartItem(form, {
    quantity,
    variantId: variantId != null ? String(variantId) : undefined,
  });

  if (sectionsToUpdate.length > 0) {
    formData.set('sections', sectionsToUpdate.join(','));
  }
  formData.set('sections_url', window.location.pathname);

  const fetchCfg = fetchConfig('javascript', {
    body: formData,
    headers: {
      Accept: 'application/json',
    },
  });

  openCartDrawerImmediately();
  dispatchOptimisticCartAdd(optimisticCartItem);

  fetch(Theme.routes.cart_add_url, fetchCfg)
    .then((response) => response.json())
    .then((response) => {
      if (response.status) {
        showFallbackAddToCartError(form, response.message);

        form.dispatchEvent(new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors));

        form.dispatchEvent(
          new CartAddEvent({}, form.getAttribute('id') || '', {
            didError: true,
            source: 'product-form-component',
            itemCount: quantity,
            productId: form.closest('[data-product-id]')?.getAttribute('data-product-id') || undefined,
            optimisticToken: optimisticCartItem?.token,
          })
        );

        return;
      }

      clearFallbackAddToCartError(form);

      const cartItemCount = getCartItemCountFromSections(response.sections);
      const cartResource = cartItemCount !== undefined ? { item_count: cartItemCount } : undefined;

      form.dispatchEvent(
        new CartAddEvent(cartResource, form.getAttribute('id') || '', {
          source: 'product-form-component',
          itemCount: quantity,
          productId: form.closest('[data-product-id]')?.getAttribute('data-product-id') || undefined,
          sections: response.sections,
          optimisticToken: optimisticCartItem?.token,
          variantId: variantId != null ? String(variantId) : undefined,
        })
      );
    })
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      pendingFallbackAddToCartForms.delete(form);
    });
}

document.addEventListener('submit', handleFallbackAddToCartSubmit, true);

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * Collects all cart section ids that should refresh after an add-to-cart action.
   * @returns {string[]}
   */
  #getCartSectionsToUpdate() {
    return getCartSectionsToUpdate();
  }

  /**
   * Extracts the updated cart item count from rendered cart sections.
   * @param {Record<string, string> | undefined} sections
   * @returns {number | undefined}
   */
  #getCartItemCountFromSections(sections) {
    return getCartItemCountFromSections(sections);
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const sectionsToUpdate = this.#getCartSectionsToUpdate();
    if (sectionsToUpdate.length > 0) {
      formData.set('sections', sectionsToUpdate.join(','));
    }
    formData.set('sections_url', window.location.pathname);

    const fetchCfg = fetchConfig('javascript', {
      body: formData,
      headers: {
        Accept: 'application/json',
      },
    });
    const optimisticCartItem = getOptimisticCartItem(form, {
      quantity: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1,
      variantId: String(formData.get('id') || overrideVariantId || ''),
    });

    openCartDrawerImmediately();
    dispatchOptimisticCartAdd(optimisticCartItem);

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              optimisticToken: optimisticCartItem?.token,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          const cartItemCount = this.#getCartItemCountFromSections(response.sections);
          const cartResource = cartItemCount !== undefined ? { item_count: cartItemCount } : undefined;

          this.dispatchEvent(
            new CartAddEvent(cartResource, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
              optimisticToken: optimisticCartItem?.token,
              variantId: id.toString(),
            })
          );

          // Keep the drawer/cart UI fast by opening from the initial add response.
          // Variant-level quantity labels can sync in the background afterward.
          void this.#fetchAndUpdateCartQuantity();
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const sectionsToUpdate = this.#getCartSectionsToUpdate();

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: sectionsToUpdate.join(','),
      sections_url: window.location.pathname,
    };

    openCartDrawerImmediately();

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        const cartItemCount = this.#getCartItemCountFromSections(response.sections);
        const cartResource = cartItemCount !== undefined ? { item_count: cartItemCount } : undefined;

        this.dispatchEvent(
          new CartAddEvent(cartResource, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );

        void this.#fetchAndUpdateCartQuantity();
      })
      .catch((error) => {
        console.error(error);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
